/**
 * Learn F1.
 *
 * A curated beginner-to-intermediate learning guide. All content is
 * hand-written per topic in src/data/learn/ — no templates, no generated
 * filler. Diagrams are purpose-built, and every topic has its own
 * 15-second explainer video served from public/media/learn.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BookOpen, Check, ChevronDown, Search } from "lucide-react";
import { LEARN_CATEGORIES, LEARN_TOPICS, findTopic, relatedTopics, searchTopics } from "../data/learn";
import { TopicDiagram } from "../components/learn/diagrams";

const VIEWED_STORAGE_KEY = "f1pulse.learn.viewedTerms";

function readViewed() {
    try {
        return new Set(JSON.parse(window.localStorage.getItem(VIEWED_STORAGE_KEY) || "[]"));
    } catch {
        return new Set();
    }
}

function writeViewed(ids) {
    try {
        window.localStorage.setItem(VIEWED_STORAGE_KEY, JSON.stringify([...ids].sort()));
    } catch {
        /* private mode */
    }
}

function TopicVideo({ video, title }) {
    if (!video?.src) return null;
    const vertical = video.orientation === "vertical";
    return (
        <video
            key={video.src}
            src={video.src}
            poster={video.poster}
            controls
            muted
            playsInline
            preload="metadata"
            aria-label={`${title}, explained in 15 seconds`}
            data-testid="learn-topic-video"
            className={`block rounded-md border border-white/10 bg-black ${vertical ? "aspect-[9/16] w-full max-w-[280px]" : "aspect-video w-full"}`}
        />
    );
}

function SidebarCategory({ category, activeTopicId, viewed, open, onToggle }) {
    const done = category.topics.filter((topic) => viewed.has(topic.id)).length;
    return (
        <div className="border-b border-white/[0.06]">
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full items-center justify-between gap-2 px-1 py-3 text-left"
            >
                <span className="text-sm font-medium text-zinc-200">{category.title}</span>
                <span className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-zinc-600">{done}/{category.topics.length}</span>
                    <ChevronDown size={14} className={`text-zinc-500 transition ${open ? "rotate-180" : ""}`} />
                </span>
            </button>
            {open ? (
                <ul className="pb-2">
                    {category.topics.map((topic) => {
                        const active = topic.id === activeTopicId;
                        return (
                            <li key={topic.id}>
                                <Link
                                    to={`/learn/${topic.id}`}
                                    className={`flex items-center gap-2 border-l-2 py-1.5 pl-3 pr-1 text-[13px] transition ${
                                        active
                                            ? "border-f1-red text-white"
                                            : "border-transparent text-zinc-400 hover:border-white/20 hover:text-zinc-200"
                                    }`}
                                >
                                    {viewed.has(topic.id) ? (
                                        <Check size={12} className="shrink-0 text-f1-red/80" />
                                    ) : (
                                        <span className="h-1 w-1 shrink-0 rounded-full bg-zinc-700" />
                                    )}
                                    <span className="truncate">{topic.title}</span>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    );
}

function TopicSection({ label, children }) {
    if (!children) return null;
    return (
        <section className="mt-8">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">{label}</h3>
            <div className="mt-3 space-y-4 text-[15px] leading-7 text-zinc-300">{children}</div>
        </section>
    );
}

function TopicPage({ topic, onViewed }) {
    useEffect(() => {
        onViewed(topic.id);
        window.scrollTo({ top: 0 });
    }, [topic.id, onViewed]);

    const related = relatedTopics(topic);

    return (
        <article className="min-w-0 max-w-2xl" data-testid="learn-topic-page">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-f1-red">
                {topic.category} · {topic.level} · {topic.readTime} min
            </p>
            <h1 className="mt-2 font-display text-4xl leading-tight tracking-tight text-white sm:text-5xl">
                {topic.title}
            </h1>
            <p className="mt-4 text-lg leading-8 text-zinc-400">{topic.summary}</p>

            <div className="mt-6 border-l-2 border-f1-red/70 bg-f1-red/[0.06] py-3 pl-4 pr-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-f1-red">Key takeaway</p>
                <p className="mt-1.5 text-[15px] leading-7 text-zinc-200">{topic.keyTakeaway}</p>
            </div>

            {topic.video ? (
                <TopicSection label="In 15 seconds">
                    <TopicVideo video={topic.video} title={topic.title} />
                </TopicSection>
            ) : null}

            <TopicSection label="The concept">
                {topic.core.map((paragraph) => (
                    <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                ))}
            </TopicSection>

            {topic.visual ? (
                <div className="mt-8">
                    <TopicDiagram visual={topic.visual} />
                </div>
            ) : null}

            <TopicSection label="How it affects the race">
                <p>{topic.affectsRace}</p>
            </TopicSection>

            <TopicSection label="A real example">
                <p>{topic.example}</p>
            </TopicSection>

            <TopicSection label="Common misconception">
                <p>{topic.misconception}</p>
            </TopicSection>

            {related.length ? (
                <TopicSection label="Related topics">
                    <div className="flex flex-wrap gap-2">
                        {related.map((relatedTopic) => (
                            <Link
                                key={relatedTopic.id}
                                to={`/learn/${relatedTopic.id}`}
                                className="rounded border border-white/10 px-3 py-1.5 text-sm text-zinc-300 transition hover:border-white/30 hover:text-white"
                            >
                                {relatedTopic.title}
                            </Link>
                        ))}
                    </div>
                </TopicSection>
            ) : null}
        </article>
    );
}

function LearnIndex({ viewed }) {
    return (
        <div className="min-w-0" data-testid="learn-index">
            <h1 className="font-display text-5xl leading-none tracking-tight text-white sm:text-6xl">Learn F1</h1>
            <p className="mt-4 max-w-xl text-lg leading-8 text-zinc-400">
                From your first Grand Prix to reading strategy like the pit wall —
                {" "}{LEARN_TOPICS.length} topics, in order, with real examples.
            </p>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
                {LEARN_CATEGORIES.map((category, index) => {
                    const done = category.topics.filter((topic) => viewed.has(topic.id)).length;
                    const first = category.topics.find((topic) => !viewed.has(topic.id)) || category.topics[0];
                    return (
                        <Link
                            key={category.id}
                            to={`/learn/${first.id}`}
                            className="group rounded-md border border-white/10 p-5 transition hover:border-white/25"
                        >
                            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                                {String(index + 1).padStart(2, "0")}
                            </p>
                            <h2 className="mt-2 text-lg font-medium text-zinc-100 group-hover:text-white">{category.title}</h2>
                            <p className="mt-1 text-sm leading-6 text-zinc-500">{category.blurb}</p>
                            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-600">
                                {done}/{category.topics.length} read
                            </p>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

export default function Learn() {
    const { topicId } = useParams();
    const navigate = useNavigate();
    const topic = useMemo(() => findTopic(topicId), [topicId]);
    const [viewed, setViewed] = useState(() => (typeof window === "undefined" ? new Set() : readViewed()));
    const [query, setQuery] = useState("");
    const [openCategory, setOpenCategory] = useState(topic?.categoryId || LEARN_CATEGORIES[0].id);

    // Legacy slugs resolve to merged topics; normalize the URL.
    useEffect(() => {
        if (topicId && topic && topic.id !== topicId) {
            navigate(`/learn/${topic.id}`, { replace: true });
        }
    }, [topicId, topic, navigate]);

    useEffect(() => {
        if (topic?.categoryId) setOpenCategory(topic.categoryId);
    }, [topic?.categoryId]);

    const markViewed = useCallback((id) => {
        setViewed((current) => {
            if (current.has(id)) return current;
            const next = new Set(current);
            next.add(id);
            writeViewed(next);
            return next;
        });
    }, []);

    const results = useMemo(() => (query.trim() ? searchTopics(query) : null), [query]);
    const totalRead = LEARN_TOPICS.filter((item) => viewed.has(item.id)).length;

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[280px_minmax(0,1fr)]">
                <aside className="lg:sticky lg:top-20 lg:self-start">
                    <div className="flex items-baseline justify-between">
                        <Link to="/learn" className="flex items-center gap-2 text-sm font-medium text-zinc-200 hover:text-white">
                            <BookOpen size={15} /> Learn F1
                        </Link>
                        <span className="font-mono text-[10px] text-zinc-600">{totalRead}/{LEARN_TOPICS.length}</span>
                    </div>
                    <label className="relative mt-4 block">
                        <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                        <input
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Search topics…"
                            className="w-full rounded border border-white/10 bg-transparent py-2 pl-8 pr-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:border-white/30 focus:outline-none"
                        />
                    </label>
                    <nav className="mt-4 max-h-[60vh] overflow-y-auto pr-1 lg:max-h-[70vh]">
                        {results ? (
                            <ul className="space-y-0.5 py-2">
                                {results.length ? results.map((result) => (
                                    <li key={result.id}>
                                        <Link
                                            to={`/learn/${result.id}`}
                                            onClick={() => setQuery("")}
                                            className="block rounded px-2 py-1.5 text-[13px] text-zinc-300 hover:bg-white/[0.04] hover:text-white"
                                        >
                                            {result.title}
                                            <span className="ml-2 font-mono text-[10px] text-zinc-600">{result.category}</span>
                                        </Link>
                                    </li>
                                )) : (
                                    <li className="px-2 py-3 text-sm text-zinc-500">No topics match “{query}”.</li>
                                )}
                            </ul>
                        ) : (
                            LEARN_CATEGORIES.map((category) => (
                                <SidebarCategory
                                    key={category.id}
                                    category={category}
                                    activeTopicId={topic?.id}
                                    viewed={viewed}
                                    open={openCategory === category.id}
                                    onToggle={() => setOpenCategory(openCategory === category.id ? null : category.id)}
                                />
                            ))
                        )}
                    </nav>
                </aside>

                {topic ? (
                    <TopicPage topic={topic} onViewed={markViewed} />
                ) : topicId ? (
                    <div className="py-16" data-testid="learn-not-found">
                        <h1 className="font-display text-3xl text-white">Topic not found</h1>
                        <p className="mt-2 text-zinc-400">
                            That page may have been merged into another topic.{" "}
                            <Link to="/learn" className="text-f1-red underline underline-offset-4">Browse all topics</Link>.
                        </p>
                    </div>
                ) : (
                    <LearnIndex viewed={viewed} />
                )}
            </div>
        </div>
    );
}
